import axios from "axios";
import swal from "sweetalert";
import config from "../../config.json";
import { getAuthToken } from "../utils/authToken";

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function updateRestoData(updatedData) {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update`,
      updatedData,
      {
        headers: authHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update restaurant data:", error);
    throw error.response?.data || { message: "An unknown error occurred." };
  }
}

export async function fetchRestoDataByUser() {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const response = await axios.get(
      `${config.dev}/v2/restaurant/data-by-user`,
      {
        headers: authHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to fetch restaurant data:", error);
    throw error.response?.data || { message: "An unknown error occurred." };
  }
}

export async function fetchMenusByRestaurant() {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const response = await axios.get(`${config.dev}/v2/menu/getAll`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Failed to fetch menu data:", error);
    throw error.response?.data || { message: "An unknown error occurred." };
  }
}

export async function updateRestaurantLogo(selectedLogo) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    // ✅ Créer un FormData et ajouter le fichier sous le nom 'logo'
    const formData = new FormData();
    formData.append("file", selectedLogo);

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-logo`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // ✅ Indispensable pour multer
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update restaurant logo:", error);
    throw (
      error.response?.data || {
        message: "An unknown error occurred while updating logo.",
      }
    );
  }
}

export async function deleteRestaurantLogo() {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await axios.delete(
      `${config.dev}/v2/restaurant/delete-logo`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur suppression logo :", error);
    throw error.response?.data || { message: "Erreur inconnue" };
  }
}

export async function updateRestaurantWifiQR(selectedWifiQR) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    // ✅ Créer un FormData et ajouter le fichier sous le nom 'file'
    const formData = new FormData();
    formData.append("file", selectedWifiQR);

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-wifiQR`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // ✅ Indispensable pour multer
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update restaurant Wi-Fi QR:", error);
    throw (
      error.response?.data || {
        message: "An unknown error occurred while updating Wi-Fi QR.",
      }
    );
  }
}

export async function updateRestaurantWelcomeVideo(
  selectedWelcomeVideo,
  startSec = 0,
  endSec = 0
) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const formData = new FormData();
    formData.append("file", selectedWelcomeVideo);
    formData.append("startSec", String(startSec || 0));
    formData.append("endSec", String(endSec || 0));

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-welcome-video`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update welcome video:", error);
    throw (
      error.response?.data || {
        message: "An unknown error occurred while updating welcome video.",
      }
    );
  }
}

export async function deleteRestaurantWelcomeVideo() {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await axios.delete(
      `${config.dev}/v2/restaurant/delete-welcome-video`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur suppression welcome video :", error);
    throw error.response?.data || { message: "Erreur inconnue" };
  }
}

export async function updateRestaurantWelcomePhoto(selectedWelcomePhoto) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const formData = new FormData();
    formData.append("file", selectedWelcomePhoto);

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-welcome-photo`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update welcome photo:", error);
    throw (
      error.response?.data || {
        message: "An unknown error occurred while updating welcome photo.",
      }
    );
  }
}

export async function deleteRestaurantWelcomePhoto() {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await axios.delete(
      `${config.dev}/v2/restaurant/delete-welcome-photo`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur suppression welcome photo :", error);
    throw error.response?.data || { message: "Erreur inconnue" };
  }
}

export async function updateRestaurantWelcomeBackgroundVideo(
  selectedVideo,
  startSec = 0,
  endSec = 0
) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const formData = new FormData();
    formData.append("file", selectedVideo);
    formData.append("startSec", String(startSec || 0));
    formData.append("endSec", String(endSec || 0));

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-welcome-background-video`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update welcome background video:", error);
    throw (
      error.response?.data || {
        message:
          "An unknown error occurred while updating welcome background video.",
      }
    );
  }
}

export async function deleteRestaurantWelcomeBackgroundVideo() {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await axios.delete(
      `${config.dev}/v2/restaurant/delete-welcome-background-video`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur suppression welcome background video :", error);
    throw error.response?.data || { message: "Erreur inconnue" };
  }
}

export async function updateRestaurantStorytellingImage(selectedImage, index) {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing.");
    }

    const formData = new FormData();
    formData.append("file", selectedImage);
    formData.append("index", String(index));

    const response = await axios.put(
      `${config.dev}/v2/restaurant/update-storytelling-image`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update storytelling image:", error);
    throw (
      error.response?.data || {
        message: "An unknown error occurred while updating storytelling image.",
      }
    );
  }
}

export async function deleteRestaurantStorytellingImage(index) {
  try {
    const token = getAuthToken();
    if (!token) throw new Error("Token manquant");

    const response = await axios.delete(
      `${config.dev}/v2/restaurant/delete-storytelling-image/${index}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erreur suppression storytelling image :", error);
    throw error.response?.data || { message: "Erreur inconnue" };
  }
}
